import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Alert, CircularProgress, MenuItem,
  Switch, FormControlLabel, Table, TableBody, TableRow, TableCell, Chip, Divider, Grid
} from '@mui/material';
import api from '../../services/api';

/**
 * Admin → Safety Monitoring (14 Sep 2026).
 *
 * How loudly alerts notify people (platform default; service groups can override it from
 * the service group list), and the daily Gen 2 safety summary: its settings, a test send,
 * and the same figures for any recent period.
 */

const unwrap = (res) => (res && res.data !== undefined ? res.data : res);

const MODE_OPTIONS = [
  { value: 'full', label: 'Full', help: 'Every notification: the first notice, overdue re-sends, priority-upgrade re-sends and escalation to supervisors and admins.' },
  { value: 'first_notice_only', label: 'First notice only', help: 'The first email, SMS and dashboard notice for each alert. Nothing is repeated or escalated.' },
  { value: 'dashboard_only', label: 'Dashboard only', help: 'Alerts are recorded and shown on dashboards. No email, SMS or real-time notice is sent.' }
];

const RANGES = [
  { hours: 24, label: 'Last 24 hours' },
  { hours: 24 * 7, label: 'Last 7 days' },
  { hours: 24 * 30, label: 'Last 30 days' }
];

const DEFAULT_SETTINGS = {
  notificationMode: 'full',
  allowServiceGroupOverride: true,
  dailySummary: { enabled: false, deliveryTime: '08:00', timezone: 'Europe/Dublin', recipients: [], sendWhenQuiet: true }
};

const SafetyMonitoring = () => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [recipientsText, setRecipientsText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [hours, setHours] = useState(24);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [runs, setRuns] = useState([]);

  const errorText = (err, fallback) => err?.response?.data?.error || err?.message || fallback;

  const loadSummary = useCallback(async (rangeHours) => {
    setSummaryLoading(true);
    try {
      setSummary(unwrap(await api.get(`/admin/safety-monitoring/summary?hours=${rangeHours}`)));
    } catch (err) {
      setError(errorText(err, 'Failed to load the summary'));
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const loadRuns = useCallback(async () => {
    try {
      setRuns(unwrap(await api.get('/admin/safety-monitoring/runs'))?.runs || []);
    } catch (_) {
      setRuns([]);
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = unwrap(await api.get('/admin/safety-monitoring/settings'));
        const loaded = { ...DEFAULT_SETTINGS, ...(data?.settings || {}) };
        loaded.dailySummary = { ...DEFAULT_SETTINGS.dailySummary, ...(data?.settings?.dailySummary || {}) };
        setSettings(loaded);
        setRecipientsText((loaded.dailySummary.recipients || []).join('\n'));
      } catch (err) {
        setError(errorText(err, 'Failed to load safety monitoring settings'));
      } finally {
        setLoading(false);
      }
    })();
    loadSummary(24);
    loadRuns();
  }, [loadSummary, loadRuns]);

  const setSummaryField = (key, value) => setSettings(s => ({ ...s, dailySummary: { ...s.dailySummary, [key]: value } }));

  const handleSave = async () => {
    setSaving(true); setError(null); setSuccess(null);
    try {
      const payload = {
        notificationMode: settings.notificationMode,
        allowServiceGroupOverride: settings.allowServiceGroupOverride,
        dailySummary: { ...settings.dailySummary, recipients: recipientsText }
      };
      const data = unwrap(await api.put('/admin/safety-monitoring/settings', payload));
      if (data?.settings) {
        setSettings(data.settings);
        setRecipientsText((data.settings.dailySummary.recipients || []).join('\n'));
      }
      setSuccess('Safety monitoring settings saved.');
      loadSummary(hours);
    } catch (err) {
      setError(errorText(err, 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    setTestSending(true); setError(null); setSuccess(null);
    try {
      const data = unwrap(await api.post('/admin/safety-monitoring/summary/send-test', {}));
      setSuccess(`Test summary sent to ${data?.to || 'you'}.`);
    } catch (err) {
      setError(errorText(err, 'Failed to send the test summary'));
    } finally {
      setTestSending(false);
    }
  };

  if (loading) {
    return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  }

  const modeHelp = MODE_OPTIONS.find(m => m.value === settings.notificationMode)?.help;
  const ds = settings.dailySummary;

  return (
    <Box sx={{ p: 3, maxWidth: 1100 }}>
      <Typography variant="h5" gutterBottom>Safety Monitoring</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        How loudly safety alerts notify people, and the daily Gen 2 safety summary. Service groups can
        override the notification mode from the service group list (the bell button).
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Alert notifications</Typography>
          <TextField
            select
            label="Platform default"
            value={settings.notificationMode}
            onChange={e => setSettings(s => ({ ...s, notificationMode: e.target.value }))}
            sx={{ minWidth: 260, mb: 1 }}
          >
            {MODE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{modeHelp}</Typography>
          <FormControlLabel
            control={<Switch checked={settings.allowServiceGroupOverride} onChange={e => setSettings(s => ({ ...s, allowServiceGroupOverride: e.target.checked }))} />}
            label="Allow service groups to override the notification mode"
          />
          <Typography variant="body2" color="text.secondary">
            Alerts are always recorded and shown on dashboards, whatever the mode. A client in several
            service groups is notified at the loudest of their groups' modes.
          </Typography>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Daily safety summary</Typography>
          <FormControlLabel
            control={<Switch checked={ds.enabled} onChange={e => setSummaryField('enabled', e.target.checked)} />}
            label="Send the daily summary by email"
          />
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={4}>
              <TextField label="Delivery time (24-hour)" value={ds.deliveryTime} onChange={e => setSummaryField('deliveryTime', e.target.value)} fullWidth helperText="Sent at the first hourly run after this time" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Timezone" value={ds.timezone} onChange={e => setSummaryField('timezone', e.target.value)} fullWidth helperText="For example Europe/Dublin" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={<Switch checked={ds.sendWhenQuiet} onChange={e => setSummaryField('sendWhenQuiet', e.target.checked)} />}
                label="Send even when there was no activity"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Recipients"
                value={recipientsText}
                onChange={e => setRecipientsText(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                helperText="One email address per line. They get every included service group. If empty, active admins get it. Service groups can add their own recipients, who get only their group."
              />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</Button>
            <Button variant="outlined" onClick={handleSendTest} disabled={testSending}>{testSending ? 'Sending…' : 'Send a test summary to me'}</Button>
          </Box>
          {runs.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Recent scheduled runs</Typography>
              {runs.slice(0, 7).map(r => (
                <Typography key={r.dateKey} variant="body2" color="text.secondary">
                  {r.dateKey}: {r.status}{r.sent ? `, ${r.sent} sent` : ''}{r.failed ? `, ${r.failed} failed` : ''}{r.reason ? ` (${r.reason})` : ''}
                </Typography>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>Summary figures</Typography>
            <TextField select size="small" value={hours} onChange={e => { const h = Number(e.target.value); setHours(h); loadSummary(h); }}>
              {RANGES.map(r => <MenuItem key={r.hours} value={r.hours}>{r.label}</MenuItem>)}
            </TextField>
            <Button size="small" onClick={() => loadSummary(hours)} disabled={summaryLoading}>Refresh</Button>
          </Box>
          {summaryLoading && <CircularProgress size={24} />}
          {!summaryLoading && summary && (
            <>
              {summary.tracesTruncated && (
                <Alert severity="info" sx={{ mb: 2 }}>Reply timings cover the first 20,000 reasoning runs in this period.</Alert>
              )}
              {(summary.sections || []).length === 0 && (
                <Typography variant="body2" color="text.secondary">No Gen 2 safety activity in this period.</Typography>
              )}
              {(summary.sections || []).map(section => (
                <Box key={section.groupId || 'none'} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{section.name}</Typography>
                    {!section.includeInSummary && <Chip size="small" label="Not in the email" />}
                    {section.activity === 0 && <Chip size="small" label="No activity" />}
                  </Box>
                  <Table size="small">
                    <TableBody>
                      {(section.rows || []).map(([label, value]) => (
                        <TableRow key={label}>
                          <TableCell sx={{ width: 320, color: 'text.secondary' }}>{label}</TableCell>
                          <TableCell>{value}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Divider sx={{ mt: 2 }} />
                </Box>
              ))}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default SafetyMonitoring;
